import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from './auth.service';
import { PermissionService } from './menu.permission.service';
import { ToasterService } from '../../shared/components/toaster/toaster.service';
import { map, Observable, take } from 'rxjs';

/**
 * Functional Role and Permission Guard
 * Enforces portal-level authorization (CRM vs Agent) and page-level permission checks.
 */
export const RoleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);
  const toaster = inject(ToasterService);

  return authService.ensureAuthenticated().pipe(
    take(1),
    map(isAuthenticated => {
      if (!isAuthenticated) {
        const userType = authService.getUserType();
        const expectedRole = route.data?.['expectedRole'] as 'crm' | 'agent' | undefined;
        const url = state?.url || (typeof window !== 'undefined' ? window.location.pathname : '');
        const isCrm = url.includes('/crm') || expectedRole === 'crm' || userType === 'crm';
        return router.parseUrl(isCrm ? '/login' : '/agentlogin');
      }

      const currentUserType = authService.getUserType(); // 'crm' | 'agent' | null
      const expectedRole = route.data?.['expectedRole'] as 'crm' | 'agent' | undefined;
      const requiredPageName = route.data?.['pageName'] as string | undefined;

      // 2. Portal-Level Authorization: Prevent Agent accessing CRM routes and vice versa
      if (expectedRole && currentUserType !== expectedRole) {
        toaster.showWarning('Access Denied: Insufficient permissions for this portal.', 'Access Restricted');
        const redirectUrl = currentUserType === 'crm' ? '/crm/admindashboard' : '/agent/dashboard';
        return router.parseUrl(redirectUrl);
      }

      // 3. Page-Level Permission Check (if pageName specified in route data)
      if (requiredPageName && !permissionService.hasAccess(requiredPageName)) {
        toaster.showWarning('You do not have permission to view this page.', 'Access Denied');
        return router.parseUrl('/404');
      }

      return true;
    })
  );
};
